<template>
    <div class="flex flex-col bg-slate-200 dark:bg-gray-900 transition-all duration-200 overflow-y-hidden overflow-x-hidden overrelative">
      <!-- 📢 Banner de Sucesso -->
      <transition name="fade">
        <div
          v-if="showSuccessBanner"
          class="fixed top-20 left-1/2 transform -translate-x-1/2 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 text-sm sm:text-base"
        >
          Movimentação realizada com sucesso!
        </div>
      </transition>
  
      <!-- 📌 Kanban Board -->
      <div
        class="kanban-container mx-5 w-full h-[calc(100vh-17rem)] md:h-[calc(100vh-14rem)] pb-16 md:pb-26 overflow-x-auto snap-x snap-mandatory scroll-smooth"
      >
        <div class="kanban-wrapper flex w-auto h-full space-x-6 pr-48">
          <div
            v-for="(status, index) in orderedStatuses"
            :key="status.id"
            :class="[
              'snap-start min-w-[280px] sm:min-w-[300px] lg:min-w-[340px] flex-shrink-0 h-full p-3 sm:p-4 rounded-lg shadow-md transition-all flex flex-col',
              status.id === 'concluido'
                ? 'bg-gray-100 dark:bg-gray-800'
                : 'bg-gray-50 dark:bg-gray-800',
              isDraggingColumn && draggedColumnId === status.id ? 'opacity-50' : 'opacity-100',
              isDraggingOverColumn === status.id ? 'border-2 border-blue-400' : ''
            ]"
            draggable="true"
            @dragstart="handleColumnDragStart($event, status.id, index)"
            @dragend="handleColumnDragEnd"
            @dragover.prevent="handleColumnDragOver($event, status.id, index)"
            @drop="handleColumnDrop($event, status.id, index)"
          >
            <!-- Column Header -->
            <div
              class="mb-3 flex items-center justify-between px-3 py-2 rounded-lg shadow-sm cursor-move"
              :class="{
                'bg-gradient-to-r from-green-400 to-green-600 text-white': status.nome === 'Concluído',
                'bg-gradient-to-r from-yellow-400 to-yellow-600 text-white': status.nome === 'Em Andamento',
                'bg-gradient-to-r from-blue-400 to-blue-600 text-white': status.nome === 'Pendente',
                'bg-gradient-to-r from-red-400 to-red-600 text-white': status.nome === 'Cancelado/Suspenso'
              }"
            >
              <h2 class="text-sm sm:text-lg font-bold">{{ status.nome }}</h2>
              <span
                class="rounded-full px-3 py-1 text-xs font-semibold bg-white text-gray-700 dark:bg-gray-800 dark:text-gray-300 shadow-md"
              >
                {{ filteredPendencias(status.id).length }}
              </span>
            </div>
  
            <div
              class="flex-1 overflow-y-auto px-1 py-2"
              @dragover.prevent="handleDragOver(status.id)"
              @drop="handleDrop($event, status.id)"
            >
              <!-- Task Cards -->
              <div
                v-for="pendencia in filteredPendencias(status.id)"
                :key="pendencia.id"
                class="group mb-3 cursor-pointer transform transition-all duration-200 hover:-translate-y-1"
                draggable="true"
                @dragstart="handleDragStart($event, pendencia.id)"
                @touchstart="handleDragStart($event, pendencia.id)"
                @touchmove="handleTouchMove"
                @dragend="handleDragEnd"
                @touchend="handleTouchEnd"
                @click="pendencia.id ? goToPendencia(pendencia.id) : null"
              >
                <div
                  class="relative rounded-lg shadow-sm hover:shadow-md bg-white dark:bg-gray-700 overflow-hidden"
                  :class="[
                    status.nome === 'Concluído'
                      ? 'border-l-4 border-green-500'
                      : new Date(pendencia.prazo) < new Date()
                      ? 'border-l-4 border-red-500'
                      : 'border-l-4 border-gray-300'
                  ]"
                >
                  <!-- Status Indicators -->
                  <div class="absolute top-3 right-3 flex gap-1">
                    <LucideAlertCircle
                      v-if="new Date(pendencia.prazo) < new Date() && status.nome !== 'Concluído'"
                      class="w-5 h-5 text-red-500 animate-pulse"
                    />
                    <LucideCheckCircle v-if="status.nome === 'Concluído'" class="w-5 h-5 text-green-500" />
                  </div>
  
                  <!-- Card Content -->
                  <div class="p-4 max-w-[300px]">
                    <!-- Header -->
                    <div class="mb-3">
                      <div class="flex items-center gap-1 mb-1">
                        <LucideClipboard class="w-4 h-4 text-gray-400 dark:text-gray-500" />
                        <h3 class="font-medium text-sm text-gray-900 dark:text-white truncate max-w-[260px]">
                          {{ pendencia.codigo }}
                        </h3>
                      </div>
                      <h3 class="font-medium text-sm text-gray-900 dark:text-white truncate max-w-[260px]">
                        {{ pendencia.status.nome }}
                      </h3>
                      <p
                        class="text-sm text-gray-600 dark:text-gray-300 line-clamp-2 max-h-[48px] overflow-hidden"
                      >
                        {{ pendencia.problema }}
                      </p>
                    </div>
  
                    <!-- Details -->
                    <div class="space-y-2 text-xs">
                      <!-- Unidade -->
                      <div class="flex items-center gap-1 text-gray-500 dark:text-gray-400">
                        <Hospital class="w-4 h-4 text-gray-400 dark:text-gray-500" />
                        <span class="font-medium">Unidade:</span>
                        <span class="truncate max-w-[200px]">
                          {{ pendencia.unidade.nome || "Não definido" }}
                        </span>
                      </div>
  
                      <!-- Responsável -->
                      <div class="flex items-center gap-1 text-gray-500 dark:text-gray-400">
                        <LucideUser class="w-4 h-4 text-gray-400 dark:text-gray-500" />
                        <span class="font-medium">Responsável:</span>
                        <span class="truncate max-w-[200px]">
                          {{ pendencia.responsavel_interno || "Não definido" }}
                        </span>
                      </div>
  
                      <!-- Fornecedor -->
                      <div class="flex items-center gap-1 text-gray-500 dark:text-gray-400">
                        <BriefcaseBusiness class="w-4 h-4 text-gray-400 dark:text-gray-500" />
                        <span class="font-medium">Fornecedor:</span>
                        <span class="truncate max-w-[200px]">
                          {{ pendencia.responsavel_externo || "Não definido" }}
                        </span>
                      </div>
  
                      <!-- Orçamento -->
                      <div class="flex items-center gap-1 text-gray-500 dark:text-gray-400">
                        <Coins class="w-4 h-4 text-gray-400 dark:text-gray-500" />
                        <span class="font-medium">Orçamento:</span>
                        <span class="truncate max-w-[200px]">
                          {{ pendencia.orcamento != null ? formatCurrency(pendencia.orcamento) : 'Não Informado' }}
                        </span>
                      </div>
  
                      <!-- GLPI -->
                      <div class="flex items-center gap-1 text-gray-500 dark:text-gray-400">
                        <Logs class="w-4 h-4 text-gray-400 dark:text-gray-500" />
                        <span class="font-medium">Nº GLPI:</span>
                        <span class="truncate max-w-[200px]">
                          {{ pendencia.glpi || "Não definido" }}
                        </span>
                      </div>
  
                      <!-- Prazo -->
                      <div
                        class="flex items-center gap-1 text-gray-500 dark:text-gray-400"
                        :class="{ 'text-red-500 dark:text-red-400': new Date(pendencia.prazo) < new Date() && status.nome !== 'Concluído' }"
                      >
                        <LucideClock class="w-4 h-4" />
                        <span class="font-medium">Prazo:</span>
                        <span>{{ formatDate(pendencia.prazo) }}</span>
                      </div>

                      <!-- Data de Criação -->
                      <div class="flex items-center gap-1 text-gray-500 dark:text-gray-400">
                        <LucideClipboard class="w-4 h-4 text-gray-400 dark:text-gray-500" />
                        <span class="font-medium">Criado em:</span>
                        <span class="truncate max-w-[200px]">
                          {{ formatarData(pendencia.data_criacao) }}
                        </span>
                      </div>

                      <!-- Última Observação -->
                      <div v-if="pendencia.ultima_observacao" class="flex items-center gap-1 text-gray-500 dark:text-gray-400">
                        <MessageSquare class="w-4 h-4 text-gray-400 dark:text-gray-500" />
                        <span class="font-medium">Última obs:</span>
                        <span class="truncate max-w-[200px]">
                          {{ pendencia.ultima_observacao }}
                        </span>
                      </div>
                    </div>
                  </div>
  
                  <!-- Progress Bar for tasks close to deadline -->
                  <div v-if="status.nome !== 'Concluído'" class="h-1 w-full bg-gray-100 dark:bg-gray-600">
                    <div
                      class="h-full rounded-3xl transition-all duration-500"
                      :class="{
                        'bg-green-500': getDaysUntilDeadline(pendencia.prazo) > 7,
                        'bg-yellow-500': getDaysUntilDeadline(pendencia.prazo) <= 7 && getDaysUntilDeadline(pendencia.prazo) > 0,
                        'bg-red-500': getDaysUntilDeadline(pendencia.prazo) <= 0
                      }"
                      :style="{ width: `${getProgressPercentage(pendencia.prazo)}%` }"
                    ></div>
                  </div>
                </div>
              </div>
            </div>
  
            <!-- Empty State -->
            <div
              v-if="filteredPendencias(status.id).length === 0"
              class="h-32 rounded-lg border-2 border-dashed border-gray-200 dark:border-gray-600 flex flex-col items-center justify-center"
            >
              <LucideInbox class="w-8 h-8 text-gray-300 dark:text-gray-500 mb-2" />
              <p class="text-sm text-gray-400 dark:text-gray-500">Sem pendências</p>
            </div>
  
            <!-- Botão Ver Mais -->
            <div v-if="hasMoreItems" class="mt-4 flex justify-center">
              <button
                @click="loadMore"
                :disabled="isLoading"
                class="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg shadow-sm transition-colors duration-200 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <span v-if="!isLoading">Ver mais</span>
                <span v-else class="flex items-center gap-2">
                  <svg
                    class="animate-spin h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path
                      class="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2-647z"
                    ></path>
                  </svg>
                  Carregando...
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>
  
      <!-- Modal de Confirmação -->
      <transition name="modal">
        <div
          v-if="showModal"
          class="fixed inset-0 bg-black/50 flex items-center justify-center p-4 dark:bg-black/70"
        >
          <div class="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md animate-fade-in p-6">
            <h3 class="text-xl font-bold text-gray-800 dark:text-white mb-4">
              ⚠️ Confirmação de Movimentação
            </h3>
            <p class="text-gray-600 dark:text-gray-300">
              Você está movendo a pendência <strong>#{{ pendenciaMovida?.codigo }}</strong> para um novo
              status.
            </p>
  
            <!-- Dropdown para escolher o novo status -->
            <div class="mt-4">
              <label class="block text-gray-700 dark:text-gray-300 text-sm font-medium">
                Selecione o Status:
              </label>
  
              <select
                v-if="statusFiltradosModal.length > 0"
                v-model="statusSelecionado"
                class="mt-2 w-full px-4 py-2 border rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-white"
              >
  <!-- esta opção será o placeholder, e só ficará visível enquanto statusSelecionado for null -->
  <option disabled :value="null">Selecione o novo status</option>

  <!-- loop nos status disponíveis -->
  <option
    v-for="status in statusFiltradosModal"
    :key="status.id"
    :value="status.id"
  >
    {{ status.nome }}
  </option>
              </select>
  
              <!-- Exibe mensagem se não houver status disponíveis -->
              <p v-else class="text-gray-500 dark:text-gray-400 text-sm mt-2">
                ⚠️ Nenhum status disponível para esta pendência. <br />
                Verifique se o tipo de pendência foi corretamente definido.
              </p>
            </div>
  
            <div class="flex justify-end gap-3 mt-6">
              <button
                @click="cancelarMovimentacao"
                class="px-6 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-all"
              >
                Cancelar
              </button>
              <button
                @click="confirmarMovimentacao"
                :disabled="!statusSelecionado"
                class="px-6 py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-all disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                Sim, Mover
              </button>
            </div>
          </div>
        </div>
      </transition>
    </div>
  </template>
  
  <script setup>
  import { useRouter } from "vue-router";
  import { ref, onMounted, computed, watch } from "vue";
  import { defineEmits } from "vue";
  import apiClient from "@/services/authService";
  import {
    LucideAlertCircle,
    LucideCheckCircle,
    LucideClock,
    Coins,
    LucideUser,
    BriefcaseBusiness,
    Hospital,
    Logs,
    LucideClipboard,
    LucideInbox,
    MessageSquare,
  } from "lucide-vue-next";
  
  const emit = defineEmits(["updateMetrics"]); // 🎯 Evento para avisar o pai a recarregar as métricas
  
  const router = useRouter();
  
  // Estados de arrastar/soltar colunas
  const isDraggingColumn = ref(false);
  const draggedColumnId = ref(null);
  const isDraggingOverColumn = ref(null);
  const statusOrder = ref([]);
  
  // Banner de sucesso
  const showSuccessBanner = ref(false);
  
  // Status e tipos carregados da API
  const statuses = ref([]);
  const tiposPendencias = ref([]);
  
  // Estado geral de pendências
  const pendencias = ref([]);
  const isLoading = ref(false);
  
  // Controles de paginação
  const currentPage = ref(1);
  const totalItems = ref(0);
  const hasMoreItems = computed(() => totalItems.value > pendencias.value.length);
  
  // Modal e movimentação
  const showModal = ref(false);
  const pendenciaMovida = ref(null);
  const statusSelecionado = ref(null);
  const novoStatus = ref(null);
  
  // Para mover itens individuais
  const isDragging = ref(null);
  const dragItem = ref(null);
  const dragStartX = ref(null);
  const dragStartY = ref(null);
  
  // 🎯 Função para buscar métricas no pai será disparada via emit("updateMetrics")
  
  const goToPendencia = (id) => {
    router.push({ name: "PendenciaDetalhes", params: { id } });
  };
  
  // Buscar lista de status (para usar no dropdown do modal)
  const fetchStatuses = async () => {
    try {
      const res = await apiClient.get("/api/v1/pending/status_pending/");
      statuses.value = res.data;
      // Inicia ordem padrão se estiver vazio
      if (statusOrder.value.length === 0) {
        statusOrder.value = res.data
          .sort((a, b) => {
            if (a.nome === "Concluído") return 1;
            if (b.nome === "Concluído") return -1;
            return 0;
          })
          .map((status) => status.id);
      }
    } catch (error) {
      console.error("Erro ao buscar status:", error);
    }
  };
  
  const orderedStatuses = computed(() => {
    return [
      { nome: "Pendente", id: "pendente" },
      { nome: "Em Andamento", id: "em_andamento" },
      { nome: "Concluído", id: "concluido" },
      { nome: "Cancelado/Suspenso", id: "cancelado" },
    ];
  });
  
  // Filtra as pendências conforme cada coluna de status
  const filteredPendencias = (statusGroup) => {
    if (!Array.isArray(pendencias.value)) return [];
    return pendencias.value.filter((p) => {
      if (!p || !p.status || !p.status.nome) return false;
      const nomeStatus = p.status.nome;
      if (statusGroup === "pendente") {
        return nomeStatus.startsWith("Aguardando");
      }
      if (statusGroup === "em_andamento") {
        return (
          nomeStatus !== "Concluído" &&
          !nomeStatus.startsWith("Aguardando") &&
          nomeStatus !== "Cancelado" &&
          nomeStatus !== "Suspenso"
        );
      }
      if (statusGroup === "concluido") {
        return nomeStatus === "Concluído";
      }
      if (statusGroup === "cancelado") {
        return nomeStatus === "Cancelado" || nomeStatus === "Suspenso";
      }
      return false;
    });
  };
  
  // 📌 Buscar todas as pendências (sempre carrega tudo)
  const fetchPendencias = async () => {
    try {
      isLoading.value = true;
      const res = await apiClient.get("/api/v1/pending/", {
        params: {
          all: 1,
          mostrar_concluidos: true,
          mostrar_excluidos: true,
        },
      });
      pendencias.value = res.data.items || [];
      totalItems.value = res.data.total;
      currentPage.value = 1;
    } catch (error) {
      console.error("Erro ao buscar pendências:", error);
    } finally {
      isLoading.value = false;
    }
  };
  
  // Observa mudanças em pendencias e, quando mudar, emite para o pai recarregar métricas
  watch(
    pendencias,
    () => {
      console.log("⚡ Pendências alteradas, solicitando recarga de métricas...");
      emit("updateMetrics");
    },
    { deep: true }
  );
  
  // 📌 Formatar datas
  const formatDate = (dateStr) => {
    if (!dateStr) return "Não definido";
    return new Date(dateStr).toLocaleDateString("pt-BR");
  };

  // 📌 Formatar data de criação
  const formatarData = (data) => {
    if (!data) return "Sem data";
    return new Date(data).toLocaleDateString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
    });
  };
  
// Formatar valores como dinheiro (R$ BRL)
const formatCurrency = (value) => {
    if (value === null || value === undefined || value === "") return "Não Informado";
    const numericValue = Number(value);
    if (isNaN(numericValue)) return "Não Informado";
    // Converte de centavos para reais dividindo por 100 e formata com o símbolo "R$"
    return new Intl.NumberFormat("pt-BR", {
        style: "currency",
        currency: "BRL",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(numericValue / 100);
};

  // 📌 Cálculos de prazo
  const getDaysUntilDeadline = (deadline) => {
    const today = new Date();
    const deadlineDate = new Date(deadline);
    const diffTime = deadlineDate - today;
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };
  
  const getProgressPercentage = (deadline) => {
    const daysLeft = getDaysUntilDeadline(deadline);
    if (daysLeft <= 0) return 100;
    if (daysLeft > 14) return 25;
    return 100 - (daysLeft / 14) * 100;
  };
  
  // 📌 Evento para soltar cards sobre colunas (destacar coluna)
  const handleDragOver = (statusId) => {
    isDraggingOverColumn.value = statusId;
  };
  
  // Variáveis/tags para touch
  const touchState = ref({
    isDragging: false,
    startX: 0,
    startY: 0,
    currentItem: null,
    currentColumn: null,
  });
  
  // 📌 Ao iniciar o arrastar (touch ou mouse)
  const handleDragStart = (event, pendenciaId) => {
    isDragging.value = pendenciaId;
    dragItem.value = event.target;
    if (event.type === "touchstart") {
      const touch = event.touches[0];
      dragStartX.value = touch.clientX;
      dragStartY.value = touch.clientY;
      event.target.style.opacity = "0.6";
      touchState.value.isDragging = true;
    } else {
      event.dataTransfer.setData("pendenciaId", pendenciaId);
      event.dataTransfer.effectAllowed = "move";
    }
  };
  
  const handleTouchMove = (event) => {
    if (!touchState.value.isDragging) return;
    const touch = event.touches[0];
    const elements = document.elementsFromPoint(touch.clientX, touch.clientY);
    const column = elements.find((el) => el.classList.contains("kanban-column"));
    if (column) {
      touchState.value.currentColumn = column.dataset.statusId;
      document.querySelectorAll(".kanban-column").forEach((col) => col.classList.remove("drag-over"));
      column.classList.add("drag-over");
    }
    event.preventDefault();
  };
  
  const handleTouchEnd = () => {
    if (isDragging.value && touchState.value.currentColumn) {
      handleDrop({ preventDefault: () => {} }, touchState.value.currentColumn);
    }
    if (dragItem.value) {
      dragItem.value.style.opacity = "1";
    }
    isDragging.value = null;
    touchState.value.isDragging = false;
    touchState.value.currentColumn = null;
  };
  
  const handleDragEnd = () => {
    isDragging.value = null;
  };
  
  // Exibir banner por 3 segundos
  const showBanner = () => {
    showSuccessBanner.value = true;
    setTimeout(() => {
      showSuccessBanner.value = false;
    }, 3000);
  };
  
  // 📌 Ao soltar no desktop
  const handleDrop = (event, newStatusId) => {
    event.preventDefault();
    let pendenciaId = isDragging.value || event.dataTransfer.getData("pendenciaId");
    if (!pendenciaId) {
      console.error("❌ Erro: Pendência não encontrada.");
      return;
    }
    const pendencia = pendencias.value.find((p) => p.id == pendenciaId);
    if (!pendencia) {
      console.error("❌ Erro: Pendência não encontrada.");
      return;
    }
    pendenciaMovida.value = pendencia;
    novoStatus.value = { id: newStatusId, nome: newStatusId };
    showModal.value = true;
  };
  
  // ✅ Confirmar movimentação — faz PUT, recarrega pendências e fecha modal
  const confirmarMovimentacao = async () => {
    if (!pendenciaMovida.value || !statusSelecionado.value) {
      console.error("❌ Erro: Nenhuma pendência ou status selecionado.");
      return;
    }
    try {
      await apiClient.put(`/api/v1/pending/update-pending//${pendenciaMovida.value.id}/`, {
        status_id: statusSelecionado.value,
        glpi: pendenciaMovida.value.glpi || "",
      });
      // Recarrega pendências do Kanban
      await fetchPendencias();
      // Fecha modal e limpa estado
      showModal.value = false;
      pendenciaMovida.value = null;
      statusSelecionado.value = null;
      novoStatus.value = null;
      // Exibe banner de sucesso
      showBanner();
      // Ao recarregar pendencias, o watch acima emitirá updateMetrics
    } catch (error) {
      console.error("❌ Erro ao atualizar status da pendência:", error);
    }
  };
  
  const statusFiltradosModal = computed(() => {
    if (!pendenciaMovida.value || !pendenciaMovida.value.tipo || !pendenciaMovida.value.tipo.id) {
      return [];
    }
    if (!novoStatus.value || !novoStatus.value.id) {
      return [];
    }
    const tipoSelecionado = tiposPendencias.value.find(
      (tipo) => tipo.id === pendenciaMovida.value.tipo.id
    );
    if (!tipoSelecionado || !Array.isArray(tipoSelecionado.status_disponiveis)) {
      return [];
    }
    return tipoSelecionado.status_disponiveis.filter((status) => {
      if (novoStatus.value.id === "pendente") {
        return status.nome.startsWith("Aguardando");
      }
      if (novoStatus.value.id === "em_andamento") {
        return (
          status.nome !== "Concluído" &&
          !status.nome.startsWith("Aguardando") &&
          status.nome !== "Cancelado" &&
          status.nome !== "Suspenso"
        );
      }
      if (novoStatus.value.id === "concluido") {
        return status.nome === "Concluído";
      }
      if (novoStatus.value.id === "cancelado") {
        return status.nome === "Cancelado" || status.nome === "Suspenso";
      }
      return false;
    });
  });
  
  // 📌 Cancelar movimentação e fechar modal
  const cancelarMovimentacao = () => {
    showModal.value = false;
    pendenciaMovida.value = null;
    novoStatus.value = null;
    statusSelecionado.value = null;
  };
  
  // Column drag-and-drop handlers
  const handleColumnDragStart = (event, statusId, index) => {
    isDraggingColumn.value = true;
    draggedColumnId.value = statusId;
    event.dataTransfer.setData("columnId", statusId);
    event.dataTransfer.setData("columnIndex", index);
  };
  
  const handleColumnDragEnd = () => {
    isDraggingColumn.value = false;
    draggedColumnId.value = null;
    isDraggingOverColumn.value = null;
  };
  
  const handleColumnDragOver = (event, statusId) => {
    if (isDraggingColumn.value) {
      isDraggingOverColumn.value = statusId;
    }
  };
  
  const handleColumnDrop = (event, targetStatusId) => {
    event.preventDefault();
    const sourceStatusId = event.dataTransfer.getData("columnId");
    if (!sourceStatusId || !targetStatusId) {
      return;
    }
    const newOrder = [...statusOrder.value];
    const sourceIndex = newOrder.indexOf(sourceStatusId);
    const targetIndex = newOrder.indexOf(targetStatusId);
    if (sourceIndex === -1 || targetIndex === -1) {
      return;
    }
    newOrder.splice(sourceIndex, 1);
    newOrder.splice(targetIndex, 0, sourceStatusId);
    statusOrder.value = newOrder;
    localStorage.setItem("statusOrder", JSON.stringify(newOrder));
    handleColumnDragEnd();
  };
  
  // Carrega tipos de pendências (para montar dropdown de statusDisponiveis)
  const fetchTiposPendencias = async () => {
    try {
      const res = await apiClient.get("/api/v1/pending/pending_types/");
      tiposPendencias.value = res.data;
    } catch (error) {
      console.error("❌ Erro ao buscar tipos de pendências:", error);
    }
  };
  
  // Função de paginação “Ver Mais”
  const loadMore = async () => {
    if (!isLoading.value && hasMoreItems.value) {
      currentPage.value++;
      // Se a API suportasse paginação incremental, usar page=currentPage
      await fetchPendencias();
    }
  };
  
  onMounted(async () => {
    await fetchTiposPendencias();
    await fetchStatuses();
    await fetchPendencias();
  
    // Carrega ordem salva de colunas
    const savedOrder = localStorage.getItem("statusOrder");
    if (savedOrder) {
      statusOrder.value = JSON.parse(savedOrder);
    }
  });
  </script>
  
  
